import { useQuery } from '@tanstack/react-query'
import { Sunrise, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient from '@/api/client'
import { formatDateHuman } from '@/lib/utils'
import type { BusinessDay } from '@/types'

interface StartTheDayEmptyStateProps {
  onStart: () => void
}

/**
 * Full-screen empty state shown when no business day is open. Designed as a
 * single, vivid call-to-action so a cashier walking up to the tablet at the
 * start of the shift sees exactly one button to tap.
 *
 * Vertically centered, large sunrise glyph, big button, and a small recap
 * of how the previous day closed so the cashier has context (without having
 * to open the dialog yet).
 */
export function StartTheDayEmptyState({ onStart }: StartTheDayEmptyStateProps) {
  const { formatCurrency } = useCurrency()

  const lastDayQuery = useQuery({
    queryKey: ['day-ops', 'history', 'last'],
    queryFn: () => apiClient.listDayOpsHistory({ limit: 1 }),
  })
  const lastClosedDay = (lastDayQuery.data?.data ?? []) as BusinessDay[]
  const previous = lastClosedDay.length > 0 ? lastClosedDay[0] : null

  return (
    <div className="relative flex min-h-[calc(100vh-4rem)] items-center justify-center overflow-hidden p-6">
      {/* Soft gradient backdrop. Pure CSS — no extra assets, ~zero render cost. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-amber-50 via-background to-background dark:from-amber-950/30 dark:via-background dark:to-background"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-12 -z-10 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-200/40 blur-3xl dark:bg-amber-500/10"
      />

      <div className="w-full max-w-2xl text-center">
        {/* Sunrise glyph */}
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/40 ring-8 ring-amber-200 dark:ring-amber-950/60">
          <Sunrise className="h-12 w-12 text-white" aria-hidden="true" />
        </div>

        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          Ready to start the day?
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-lg text-muted-foreground">
          Tap the button below when the shop opens. We'll keep track of every sale
          until you finish at the end of the day.
        </p>

        {previous && (
          <div className="mx-auto mt-6 inline-flex flex-wrap items-center justify-center gap-2 rounded-full border bg-card px-4 py-2 text-sm shadow-sm">
            <span className="text-muted-foreground">
              Yesterday ({formatDateHuman(previous.business_date)}) ended with
            </span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatCurrency(previous.counted_cash ?? 0)}
            </span>
            <span className="text-muted-foreground">in the drawer.</span>
          </div>
        )}

        <div className="mt-10 flex flex-col items-center gap-3">
          <Button
            size="lg"
            onClick={onStart}
            className="h-16 w-full max-w-xs px-10 text-xl font-semibold shadow-lg shadow-emerald-500/30 bg-emerald-600 hover:bg-emerald-700 text-white sm:w-auto"
          >
            <Sunrise className="mr-2 h-6 w-6" aria-hidden="true" />
            Start the day
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
          </Button>
          <p className="text-sm text-muted-foreground">
            It only takes a few seconds.
          </p>
        </div>
      </div>
    </div>
  )
}
